import type { ComponentChild, FunctionComponent, TargetedEvent, TargetedKeyboardEvent } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import type { TemplateFunction } from 'Components/types';
import Step, { type MathOps, type StepInput } from 'Models/step';
import type { FacetSliderMode } from 'Models/uiConfig/facetsConfig';
import { cloneSafe } from 'Utils/components';
import type { MinMax } from 'Utils/ranged';

type InputParams = {
  rawValue: string;
};

export type Params = {
  inputMin?: (templ: TemplateFunction<InputParams>) => ComponentChild;
  inputMax?: (templ: TemplateFunction<InputParams>) => ComponentChild;
  setCustomRange: () => void;
};

type Props = {
  template: TemplateFunction<Params>;
  bounds?: MinMax;
  rawBounds?: MinMax;
  step?: StepInput;
  selectedValues?: MinMax;
  mode?: FacetSliderMode;
  handleCustomRange: (selection: MinMax) => void;
};

const inputErrorClass = 'cm_input-error';

const RangedInputs: FunctionComponent<Props> = ({
  template,
  bounds: [min, max] = ['0', '*'],
  rawBounds,
  step: rawStep,
  selectedValues,
  mode,
  handleCustomRange,
}) => {
  const [rawMin, rawMax] = rawBounds ?? [min, max];
  const inputsRef = useRef<[HTMLInputElement | null, HTMLInputElement | null]>([null, null]);
  const step = useMemo(() => new Step(rawStep), [rawStep]);

  const [currentBounds, setCurrentBounds] = useState<MinMax>(['', '']);
  const [errorIndicator, setErrorIndicator] = useState<boolean>(false);

  useEffect(() => {
    if (selectedValues) {
      inputsRef.current.forEach((input) => input?.classList.remove(inputErrorClass));
      const cleanSelection = selectedValues.map((sel) => (sel === '*' ? '' : sel)) as MinMax;
      setCurrentBounds(cleanSelection);
    }
  }, [selectedValues]);

  if (min === max && max !== '*') {
    return null;
  }

  const setCustomRange = () => {
    if (errorIndicator) {
      return;
    }

    const rawSelection = currentBounds.map((bound, i) => {
      if (bound === '' || (i === 0 && min === '*') || (i === 1 && max === '*')) {
        return bound || '*';
      }

      return +bound < +min ? min : +bound > +max ? max : bound;
    }) as MinMax;

    const selection = inputsRef.current.map((input, i) =>
      input && input.step && rawSelection[i] !== '*'
        ? step.fit(+rawSelection[i], ['floor', 'ceil'][i] as MathOps)
        : rawSelection[i],
    ) as MinMax;

    handleCustomRange(selection);
  };

  const onInputChange = (inputValue: string, changingBoundIndex: number) => {
    const newBounds = [...currentBounds] as MinMax;
    newBounds[changingBoundIndex] = inputValue;
    setCurrentBounds(newBounds);

    inputsRef.current[0]?.classList.toggle(inputErrorClass, newBounds[0] !== '' && isNaN(+newBounds[0]));
    inputsRef.current[1]?.classList.toggle(inputErrorClass, newBounds[1] !== '' && isNaN(+newBounds[1]));

    const isChangingBoundValid = !newBounds.includes('') && +newBounds[0] > +newBounds[1];
    inputsRef.current[changingBoundIndex]?.classList.toggle(inputErrorClass, isChangingBoundValid);
    setErrorIndicator(isChangingBoundValid);
  };

  const [inputMin, inputMax] = ['min', 'max'].map((bound, i) => (templ: TemplateFunction<InputParams>) => {
    const inputProps = templ.call({ rawValue: [rawMin, rawMax][i] })?.props;
    const props = {
      placeholder: [min, max][i],
      ...inputProps,
      type: 'number',
      value: currentBounds[i],
      step: step.numeric,
      min,
      max,
      onChange: (e: TargetedEvent<HTMLInputElement, Event>) => onInputChange(e.currentTarget.value, i),
      ['data-cm-role']: `input-${bound}`,
      ref: (el: HTMLInputElement | null) => {
        inputsRef.current[i] = el;
      },
    };

    return <input {...props} key={`input-${bound}`} />;
  });

  const onKeyDown = (e: TargetedKeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      setCustomRange();
    }
  };

  if (mode === 'LowerBound') {
    return cloneSafe(template.call({ inputMin, setCustomRange }), null, { onKeyDown });
  }

  if (mode === 'UpperBound') {
    return cloneSafe(template.call({ inputMax, setCustomRange }), null, { onKeyDown });
  }

  return cloneSafe(template.call({ inputMin, inputMax, setCustomRange }), null, { onKeyDown });
};

export default RangedInputs;
